import * as React from 'react';
import { Box, Typography, IconButton, makeStyles, createStyles } from '@material-ui/core';

import { alpha } from '@material-ui/core/styles/colorManipulator';
import cn from 'classnames';
import { InfoOutlined as InfoIcon, HelpOutlineOutlined as QuestionIcon, Close as CloseIcon } from '@material-ui/icons';
import Collapse from '../Collapse/Collapse';
import { TranquiTheme } from '../../containers/App/theme';
import InformationIcon from '../../assets/icons/InformationIcon';

export interface AlertProps {
  className?: string;
  filled?: boolean;
  elevation?: number;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'gray' | 'none';
  icon?: 'info' | 'infoFilled' | 'question';
  iconColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'gray' | 'none';
  iconFilled?: boolean;
  title?: string;
  closable?: boolean;
  importantText?: boolean
  titleClassName?: string;
  contentClassName?: string;
  iconClassName?: string;
}

const Alert: React.FC<AlertProps> = (props) => {
  const {
    className, children, filled, iconFilled,
    elevation, icon, title, iconColor, iconClassName,
    closable, titleClassName, contentClassName, ...others
  } = props;

  let { color, importantText } = props;

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles({ elevation });
  const [ closed, setClosed ] = React.useState(false);
  const handleClose = () => setClosed(true);
  if (elevation && !color) color = 'none';
  if (!color) color = 'gray';
  if (!importantText) importantText = false;

  const Icon = icon === 'info' ? InfoIcon : QuestionIcon;
  const content = (
    <Box
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...others}
      className={cn(classes.root, className, classes[color], filled && (classes as any)[`${color}Filled`], elevation && classes.elevation)}
    >
      <Box display="flex" alignItems="flex-start">
        {icon
            && icon === 'infoFilled'
          ? <InformationIcon outlined={!iconFilled} className={cn('mr-1', (classes as any)[`${iconColor}Text`], iconClassName)} />
          : <Icon className={cn('mr-1', (classes as any)[`${iconColor}Text`], iconClassName)} />}

        <Box flexGrow={1} className={cn(contentClassName)} alignSelf="center">
          {title && <Typography className={cn(classes.title, titleClassName)}>{title}</Typography>}
          {importantText && <strong>IMPORTANTE:</strong>}
          {' '}
          {children}
        </Box>
        {closable
        && <IconButton className={classes.close} onClick={handleClose}><CloseIcon /></IconButton>}
      </Box>
    </Box>
  );

  return (closable
    ? <Collapse in={!closed}>{content}</Collapse>
    : content);
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  root: {
    borderRadius: theme.shape.borderRadius,
    border: '1px solid',
    padding: theme.spacing(1),
    lineHeight: 1.2,
    position: 'relative',
  },
  title: {
    color: theme.palette.indigo.main,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  close: {
    position: 'absolute',
    top: 2,
    right: 2,
    padding: 4,
  },
  elevation: { boxShadow: ({ elevation }: any) => theme.shadows[elevation] || 'none' },
  primary: { borderColor: theme.palette.primary.main },
  primaryFilled: { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
  primaryText: { color: theme.palette.primary.main },
  secondary: { borderColor: theme.palette.secondary.main },
  secondaryFilled: { backgroundColor: alpha(theme.palette.secondary.main, 0.1) },
  secondaryText: { color: theme.palette.secondary.main },
  error: { borderColor: theme.palette.salmon.main },
  errorFilled: { backgroundColor: alpha(theme.palette.salmon.main, 0.1) },
  errorText: { color: theme.palette.salmon.main },
  warning: { borderColor: theme.palette.warning.main },
  warningFilled: { backgroundColor: alpha(theme.palette.warning.main, 0.1) },
  warningText: { color: theme.palette.warning.main },
  gray: { borderColor: theme.palette.grey[400] },
  grayFilled: { backgroundColor: alpha(theme.palette.grey[400], 0.1) },
  grayText: { color: theme.palette.grey[400] },
  none: { borderColor: 'transparent' },
}));

export default Alert;
